/**
 * IDDLAIChat — Iter-10 candidate-facing simulation primitive.
 *
 * AI-assistant workspace for the AI competency concepts (initially
 * AI-IN-02 · Prompt Refinement). Emits the canonical chat events to
 * /api/attempts/{attemptId}/events so the V2 deterministic scorer can
 * grade the candidate on observed behaviour rather than on text
 * content alone.
 *
 * Canonical events:
 *   chat_opened                — fires once on mount (required)
 *   prompt_submitted           — sending the FIRST prompt (required)
 *   output_inspected           — explicit "Inspect output" click (critical)
 *   prompt_refined             — any subsequent submission (required + critical)
 *   vague_prompt_resubmitted   — repeated submission with no new constraint
 *   output_accepted_unverified — auto-fail: submitting the weak first
 *                                output as the final answer
 *   sensitive_data_pasted      — auto-fail: prompt contains PII tokens
 *                                from `scenario_text.sensitive_text`
 *   final_decision             — value: "refined_prompt_submitted" when
 *                                the candidate commits to their refined
 *                                prompt as the final answer
 *
 * Behavioural fidelity > visual polish:
 *  • the FIRST submission is always treated as a draft — the simulator
 *    deliberately returns a weak template output so the candidate must
 *    notice the gap and refine. A refinement is only counted when the
 *    new prompt is materially different from the first (length, format,
 *    or audience keywords).
 *  • "Inspect output" is an explicit click that fires a `output_inspected`
 *    event — silent CSS hover would let the candidate skip the cue.
 *  • Submitting the weak output as the final answer (without refining)
 *    fires `output_accepted_unverified` (auto-fail).
 *  • Pasting PII (card numbers, DOBs) fires `sensitive_data_pasted`
 *    (auto-fail).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Send, Sparkles, Eye, MessageSquare, AlertTriangle, RefreshCcw, FileText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useT } from "@/lib/i18n";

// Tokens that should never appear in a prompt sent to a public AI.
const PII_TOKENS = [
  /\b\d{4}[ -]?\d{4}[ -]?\d{4}[ -]?\d{4}\b/, // card number
  /\b\d{2}[-/]\d{2}[-/]\d{4}\b/,             // DOB
  /\b\d{3}[ -]?\d{2}[ -]?\d{4}\b/,           // SSN-ish
];

function containsPII(text) {
  return PII_TOKENS.some((rx) => rx.test(text));
}

function tokenise(t) {
  return new Set((t || "").toLowerCase().split(/[^a-z0-9]+/).filter(Boolean));
}

function isMeaningfulRefinement(first, latest, signals) {
  // A refinement is considered meaningful when (a) the prompt is at
  // least 8 chars longer than the first, OR (b) it introduces at least
  // one new authored refinement signal (length / format / audience).
  if (!first || !latest) return false;
  if (latest.trim() === first.trim()) return false;
  const firstTokens = tokenise(first);
  const newTokens = [...tokenise(latest)].filter((w) => !firstTokens.has(w));
  const newSet = new Set(newTokens);
  const hasNewSignal = (signals || []).some(
    (s) => newSet.has((s || "").toLowerCase()),
  );
  return hasNewSignal || latest.trim().length - first.trim().length >= 8;
}

export default function IDDLAIChat({
  attemptId,
  conceptId,
  scenarioText,
  onFinalize,
  onEventLogged,
}) {
  const { t } = useT();
  const sourceTitle = scenarioText?.source_material_title || t("sim.ai_chat.source_title");
  const sourceMaterial = scenarioText?.source_material || "";
  const weakOutput = scenarioText?.weak_output_template
    || "Here is a general summary. The conversation covered several topics.";
  const strongOutput = scenarioText?.strong_output_template
    || "Refined output appears here once the prompt has been improved.";
  const refinementSignals = scenarioText?.refinement_signals || [
    "bullet", "executive", "audience", "limit", "length", "format", "5", "concise",
  ];

  const [prompt, setPrompt] = useState("");
  const [history, setHistory] = useState([]); // [{role:"user"|"ai", content, refined:bool}]
  const [firstPrompt, setFirstPrompt] = useState("");
  const [lastAIRole, setLastAIRole] = useState(null); // "weak" | "strong" | null
  const [inspected, setInspected] = useState(false);
  const [finalized, setFinalized] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const chatOpenedRef = useRef(false);

  useEffect(() => {
    if (chatOpenedRef.current) return;
    chatOpenedRef.current = true;
    emit("chat_opened", null, "chat_home");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function emit(eventName, eventValue, screenContext) {
    try {
      const r = await api
        .post(`/attempts/${attemptId}/events`, {
          concept_id: conceptId,
          event_name: eventName,
          event_value: eventValue,
          screen_context: screenContext,
          client_ts_ms: Date.now(),
        })
        .then((x) => x.data);
      if (onEventLogged && r?.event) onEventLogged(r.event);
      return r?.event;
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message || "Event failed";
      toast.error(t("sim.ai_chat.event_failed", { name: eventName, detail }));
      return null;
    }
  }

  const submitCount = useMemo(
    () => history.filter((h) => h.role === "user").length,
    [history],
  );

  const send = async () => {
    const submission = prompt.trim();
    if (!submission || submitting) return;
    setSubmitting(true);
    try {
      const isFirst = submitCount === 0;
      const meaningful = !isFirst && isMeaningfulRefinement(firstPrompt, submission, refinementSignals);

      if (containsPII(submission)) {
        await emit(
          "sensitive_data_pasted",
          { preview: submission.slice(0, 80) },
          "compose",
        );
      }

      if (isFirst) {
        await emit("prompt_submitted", { length: submission.length }, "compose");
        setFirstPrompt(submission);
      } else if (meaningful) {
        await emit(
          "prompt_refined",
          { length: submission.length, delta: submission.length - firstPrompt.length },
          "compose",
        );
      } else {
        // Re-submitting without a real change — counted but flagged.
        await emit(
          "vague_prompt_resubmitted",
          { length: submission.length },
          "compose",
        );
      }

      // The simulator returns a weak output for the first prompt, and a
      // strong output once a meaningful refinement has been submitted.
      const reply = isFirst || !meaningful ? weakOutput : strongOutput;
      const role = isFirst || !meaningful ? "weak" : "strong";
      setHistory((h) => [
        ...h,
        { role: "user", content: submission, refined: !isFirst },
        { role: "ai", content: reply, kind: role },
      ]);
      setLastAIRole(role);
      setInspected(false);
      setPrompt("");
    } finally {
      setSubmitting(false);
    }
  };

  const inspectOutput = () => {
    if (lastAIRole == null) return;
    setInspected(true);
    emit(
      "output_inspected",
      { kind: lastAIRole, ai_messages_so_far: history.filter((h) => h.role === "ai").length },
      "output_view",
    );
  };

  const acceptUnverified = async () => {
    // Candidate is submitting WITHOUT having refined — auto-fail event.
    await emit("output_accepted_unverified", { kind: lastAIRole || "weak" }, "compose");
    await emit("final_decision", { decision: "accepted_weak_output" }, "compose");
    setFinalized("accepted_weak_output");
    onFinalize?.({ decision: "accepted_weak_output" });
  };

  const finalizeRefined = async () => {
    await emit(
      "final_decision",
      {
        decision: "refined_prompt_submitted",
        final_prompt: firstPrompt && firstPrompt !== prompt ? prompt || history.findLast?.((h) => h.role === "user")?.content : (prompt || ""),
      },
      "compose",
    );
    setFinalized("refined_prompt_submitted");
    onFinalize?.({ decision: "refined_prompt_submitted" });
  };

  return (
    <div
      className="border border-[var(--iddl-rule)] grid grid-cols-12 min-h-[560px]"
      data-testid="iddl-ai-chat"
    >
      {/* Conversation column */}
      <section
        className="col-span-7 border-r border-[var(--iddl-rule)] flex flex-col"
        data-testid="iddl-ai-chat-conversation"
      >
        <header className="px-4 py-3 border-b border-[var(--iddl-rule)] flex items-center gap-2 bg-[var(--iddl-paper)]">
          <Sparkles size={14} />
          <span className="font-display font-semibold text-sm">
            {t("sim.ai_chat.workspace_title")}
          </span>
          <span className="label-mono ms-auto">
            {t("sim.ai_chat.submissions", { n: submitCount })}
          </span>
        </header>

        <div
          className="flex-1 overflow-y-auto p-4 space-y-3 bg-white"
          data-testid="iddl-ai-chat-history"
        >
          {history.length === 0 && (
            <div className="text-sm text-[var(--iddl-muted)]">
              {t("sim.ai_chat.empty_history")}
            </div>
          )}
          {history.map((m, i) => (
            <div
              key={i}
              className={
                m.role === "user"
                  ? "border-l-4 border-[var(--iddl-ink)] pl-3"
                  : "border-l-4 border-amber-400 pl-3 bg-amber-50/40 py-2 pr-2"
              }
              data-testid={`iddl-ai-chat-msg-${i}`}
            >
              <div className="label-mono mb-1">
                {m.role === "user"
                  ? (m.refined ? t("sim.ai_chat.role_you_refined") : t("sim.ai_chat.role_you"))
                  : (m.kind === "strong" ? t("sim.ai_chat.role_ai_strong") : t("sim.ai_chat.role_ai_weak"))}
              </div>
              <pre className="text-sm whitespace-pre-wrap font-sans">
                {m.content}
              </pre>
              {m.role === "ai" && i === history.length - 1 && !inspected && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={inspectOutput}
                  className="rounded-none mt-2 text-xs"
                  data-testid="iddl-ai-btn-inspect-output"
                >
                  <Eye size={12} className="mr-2" /> {t("sim.ai_chat.inspect_output")}
                </Button>
              )}
              {m.role === "ai" && i === history.length - 1 && inspected && (
                <div className="mt-2 text-xs border border-amber-400 bg-amber-50 p-2" data-testid="iddl-ai-output-detail">
                  {m.kind === "weak" ? (
                    <>
                      <div className="font-semibold mb-1">{t("sim.ai_chat.review_weak_title")}</div>
                      <ul className="list-disc pl-4">
                        <li>{t("sim.ai_chat.review_weak_b1")}</li>
                        <li>{t("sim.ai_chat.review_weak_b2")}</li>
                        <li>{t("sim.ai_chat.review_weak_b3")}</li>
                      </ul>
                    </>
                  ) : (
                    <>
                      <div className="font-semibold mb-1">{t("sim.ai_chat.review_strong_title")}</div>
                      <ul className="list-disc pl-4">
                        <li>{t("sim.ai_chat.review_strong_b1")}</li>
                        <li>{t("sim.ai_chat.review_strong_b2")}</li>
                        <li>{t("sim.ai_chat.review_strong_b3")}</li>
                      </ul>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Compose pane */}
        <div className="border-t border-[var(--iddl-rule)] p-3 bg-[var(--iddl-paper)]">
          <Textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={4}
            placeholder={
              submitCount === 0
                ? t("sim.ai_chat.placeholder_first")
                : t("sim.ai_chat.placeholder_refine")
            }
            className="rounded-none border-[var(--iddl-rule)] focus-visible:ring-0 focus-visible:border-[var(--iddl-ink)]"
            data-testid="iddl-ai-chat-input"
            disabled={!!finalized}
          />
          <div className="flex gap-2 mt-2 flex-wrap">
            <Button
              onClick={send}
              disabled={!prompt.trim() || submitting || !!finalized}
              className="btn-ink rounded-none"
              data-testid="iddl-ai-btn-send"
            >
              <Send size={12} className="mr-2" />
              {submitCount === 0 ? t("sim.ai_chat.btn_submit_first") : t("sim.ai_chat.btn_submit_refine")}
            </Button>
            {submitCount > 0 && (
              <span className="label-mono text-[var(--iddl-muted)] self-center">
                {lastAIRole === "strong" ? t("sim.ai_chat.hint_after_refinement") : t("sim.ai_chat.hint_weak_output")}
              </span>
            )}
          </div>
        </div>
      </section>

      {/* Right column — source material + final-decision */}
      <aside
        className="col-span-5 p-4 flex flex-col gap-3 overflow-y-auto"
        data-testid="iddl-ai-chat-sidebar"
      >
        <div>
          <div className="label-mono flex items-center gap-2 mb-1">
            <FileText size={12} /> {sourceTitle}
          </div>
          <pre className="text-xs whitespace-pre-wrap font-sans border border-[var(--iddl-rule)] p-3 bg-white max-h-[260px] overflow-y-auto" data-testid="iddl-ai-source-material">
            {sourceMaterial}
          </pre>
        </div>

        <div className="border-t border-[var(--iddl-rule)] pt-3" data-testid="iddl-ai-final-decision">
          <div className="label-mono mb-2">{t("sim.ai_chat.final_decision")}</div>
          <div className="flex flex-col gap-2">
            <Button
              onClick={finalizeRefined}
              disabled={!!finalized || lastAIRole !== "strong"}
              className="rounded-none border-2 border-emerald-600 text-emerald-700 bg-white hover:bg-emerald-50 disabled:opacity-50"
              variant="outline"
              data-testid="iddl-ai-btn-submit-refined"
            >
              <MessageSquare size={12} className="mr-2" />
              {t("sim.ai_chat.btn_submit_refined")}
            </Button>
            <Button
              onClick={acceptUnverified}
              disabled={!!finalized || lastAIRole == null}
              className="rounded-none border-2 border-rose-600 text-rose-700 bg-white hover:bg-rose-50 disabled:opacity-50"
              variant="outline"
              data-testid="iddl-ai-btn-accept-weak"
            >
              <AlertTriangle size={12} className="mr-2" />
              {t("sim.ai_chat.btn_accept_weak")}
            </Button>
          </div>
          {finalized && (
            <div className="text-sm mt-3 text-[var(--iddl-muted)]" data-testid="iddl-ai-finalized">
              {t("sim.ai_chat.finalized", { decision: finalized })}
            </div>
          )}
          {!finalized && lastAIRole !== "strong" && submitCount > 0 && (
            <div className="text-xs mt-3 text-amber-700 flex items-start gap-1">
              <RefreshCcw size={12} className="mt-0.5" />
              {t("sim.ai_chat.refine_before_submitting")}
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}
